// ============================================================================
// NET SALARY CALCULATOR - Country Tax Modules
// ============================================================================

// Types
export * from "./types";

// Utilities
export * from "./utils";

// Country-specific calculations
export { calculateSpainTax, SPAIN_TAX_CONFIG_2025 } from "./spain";
export { calculateUSATax, USA_TAX_CONFIG } from "./usa";
export { calculateUKTax, UK_TAX_CONFIG } from "./uk";
export { calculateGermanyTax, GERMANY_TAX_CONFIG } from "./germany";
export { calculateFranceTax, FRANCE_TAX_CONFIG } from "./france";
export { calculateItalyTax, ITALY_TAX_CONFIG } from "./italy";
export { calculateSwitzerlandTax, SWITZERLAND_TAX_CONFIG } from "./switzerland";

// ============================================================================
// UNIFIED TAX CALCULATION
// ============================================================================

import { TaxCalculationResult } from "./types";
import { calculateSpainTax } from "./spain";
import { calculateUSATax } from "./usa";
import { calculateUKTax } from "./uk";
import { calculateGermanyTax } from "./germany";
import { calculateFranceTax } from "./france";
import { calculateItalyTax } from "./italy";
import { calculateSwitzerlandTax } from "./switzerland";

export interface TaxCalculationParams {
  country: string;
  grossIncome: number;
  contractType?: string;
  age?: number;
  familyStatus: string;
  dependents: number;
  hasDisability?: boolean;
  disabilityDegree?: number;
  geographicMobility?: boolean;
  movedResidence?: boolean;
}

/**
 * Calcula impuestos y cotizaciones según el país seleccionado
 */
export function calculateTaxByCountry(params: TaxCalculationParams): TaxCalculationResult {
  const {
    country,
    grossIncome,
    contractType = "permanent",
    age = 30,
    familyStatus,
    dependents,
    hasDisability = false,
    disabilityDegree = 0,
    geographicMobility = false,
    movedResidence = false,
  } = params;

  switch (country) {
    case "Spain":
      return calculateSpainTax({
        grossIncome,
        contractType,
        age,
        familyStatus,
        dependents,
        hasDisability,
        disabilityDegree,
        geographicMobility,
        movedResidence,
      });

    case "USA":
      return calculateUSATax({
        grossIncome,
        familyStatus,
        dependents,
      });

    case "UK":
      return calculateUKTax({
        grossIncome,
        familyStatus,
        dependents,
      });

    case "Germany":
      return calculateGermanyTax({
        grossIncome,
        familyStatus,
        dependents,
        age,
      });

    case "France":
      return calculateFranceTax({
        grossIncome,
        familyStatus,
        dependents,
      });

    case "Italy":
      return calculateItalyTax({
        grossIncome,
        familyStatus,
        dependents,
      });

    case "Switzerland":
      return calculateSwitzerlandTax({
        grossIncome,
        familyStatus,
        dependents,
        age,
      });

    default:
      // Default calculation for unsupported countries
      return {
        tax: 0,
        taxRate: 0,
        socialSecurity: 0,
        allowances: 0,
      };
  }
}

/**
 * Lista de países soportados con sus monedas
 */
export const SUPPORTED_COUNTRIES = {
  Spain: { currency: "EUR", name: "España" },
  USA: { currency: "USD", name: "Estados Unidos" },
  UK: { currency: "GBP", name: "Reino Unido" },
  Germany: { currency: "EUR", name: "Alemania" },
  France: { currency: "EUR", name: "Francia" },
  Italy: { currency: "EUR", name: "Italia" },
  Switzerland: { currency: "CHF", name: "Suiza" },
} as const;

export type SupportedCountry = keyof typeof SUPPORTED_COUNTRIES;
